import {_} from 'lodash';
import * as ko from "knockout";
import {IMessageData, IObjectData, ISearchObjectParams, ISystem, ISystemRole, TDeleted} from "../../../index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {IDialogOptions} from "../../dialog/index";
import {nsSystemAccessSearch} from "../roles/search/nsSystemAccessSearch";
import {DELETING_MESSAGE, REQUEST_MESSAGE} from "../../../constants/Language";
import PromiseRequest from "../../../req/PromiseRequest";
import {global} from "../../../appContext";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId, SPINNER, SYSTEM_MEGA_EADM} from "../../../constants";
import {ButtonManager} from "../../../util/ButtonManager";
import {getCancelButton, getContinueButton} from "../../../constants/RouteDefinitions";
import {createErrorMessage, createErrorString, getPercent, isPartOf} from "../../../util/misc";
import {getSearchObjectParams, getSearchSystemParams} from "../../../util/request";
import {nsRuleset} from "../../ruleset/nsRuleset";
import {nsRoute} from "../../../util/route";
import pubsub from "../../../pubsub";
import ButtonOrder = nsRoute.ButtonOrder;
import purge_permissionsHtml from './purge-permissions.html?raw';

export const template = purge_permissionsHtml;

interface IParams {
    dialog: IDialogOptions
    roles: ISystemRole[]
}

type TStep = 'init' | 'introduction' | 'process' | 'finish'

interface ISelectedRole {
    numActiveUsers: number
    numInactiveUsers: number
    numTotalUsers: number
    req_deleterole: () => Promise<any>
    req_purgesystemrolepermissions: () => Promise<any>
    req_searchobject_A: () => Promise<number[]> // active users
    req_searchobject_B: () => Promise<number[]> // inactive users
    res_A: number[]
    res_B: number[]
    role: ISystemRole
    system: ISystem | undefined
}

export class viewModel {
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly req_searchsystem = global.getPromiseRequest('searchsystem')

    private readonly pageButtons: ButtonManager = new ButtonManager()

    private readonly step: KnockoutObservable<TStep | null> = ko.observable(null);
    private readonly toggleOverride: KnockoutObservable<boolean> = ko.observable(true);

    private selectedRoles!: ISelectedRole[]
    private requests: PromiseRequest[] = []

    private numTotalUsers!: number
    private systems!: ISystem[];

    private submitSuccess: ISelectedRole[] = []
    private submitError: { role: ISelectedRole, reason: string }[] = []

    constructor(private params: IParams) {
        this.kosub.subscribe(this.step, this.onStep.bind(this))

        this.pageButtons.add(getCancelButton(this.params.dialog.doClose, {
            enabled: () => !this.isStep('process'),
            validator: () => !this.isStep('finish'),
        }))

        this.pageButtons.add(getContinueButton({
            onClick: () => this.step('process'),
            cssClass: 'danger',
            enabled: () => this.isReady(),
            label: 'yes, continue',
            validator: () => this.isStep('introduction'),
        }))

        this.pageButtons.add(getCancelButton(this.params.dialog.doClose, {
            enabled: () => this.isReady(),
            icon: 'fa-times',
            label: 'close',
            order: ButtonOrder.CONTINUE,
            validator: () => this.isStep('finish'),
        }))

        this.step('init')
    }

    private onStep() {
        switch (this.step()) {
            case "init": {
                this.isReady(false)
                this.messageData({message: REQUEST_MESSAGE})

                Promise.resolve()
                    .then(() => {
                        return this.req_searchsystem
                            .request(getSearchSystemParams({
                                resultsprpage: MAX_RESULTS_PER_PAGE,
                            }))
                            .then((data: ISystem[]) => {
                                this.systems = data
                            })
                    })
                    .then(() => {
                        this.selectedRoles = this.params.roles
                            .map(o => {
                                const role: ISelectedRole = {
                                    numActiveUsers: 0,
                                    numInactiveUsers: 0,
                                    numTotalUsers: 0,
                                    req_deleterole: this.deleteRoleFactory(o),
                                    req_purgesystemrolepermissions: this.purgeSystemRolePermissionsFactory(o),
                                    req_searchobject_A: this.searchObjectFactory(o, 1),
                                    req_searchobject_B: this.searchObjectFactory(o, -1),
                                    res_A: [],
                                    res_B: [],
                                    role: o,
                                    system: this.systems.find(s => s.id == o.systemid),
                                };
                                return role;
                            })
                            .filter((o: ISelectedRole) => o.system?.name != SYSTEM_MEGA_EADM)
                        if (!this.selectedRoles.length) {
                            this.messageData({message: 'no data'})
                            return
                        }
                        const items = _.clone(this.selectedRoles)
                        const doNext = (): Promise<any> => {
                            if (!items.length) {
                                return Promise.resolve()
                            }
                            const item = items.shift()!
                            this.updateLoadingPercent()
                            return Promise.resolve()
                                .then(() => item.req_searchobject_A().then((data: number[]) => item.res_A = data))
                                .then(() => item.req_searchobject_B().then((data: number[]) => item.res_B = data))
                                .then(() => {
                                    item.numActiveUsers = item.res_A.length
                                    item.numInactiveUsers = item.res_B.length
                                    item.numTotalUsers = item.numActiveUsers + item.numInactiveUsers
                                    this.updateLoadingPercent()
                                    return doNext()
                                })
                        }

                        doNext().then(() => {
                            this.numTotalUsers = this.selectedRoles.reduce((num: number, role: ISelectedRole) => {
                                return num + role.res_A.length + role.res_B.length
                            }, 0)
                            this.messageData(null)
                            this.step('introduction')
                        })
                    })
                    .catch((reason: string) => {
                        this.messageData(createErrorMessage(String(reason)))
                    })


                break
            }

            case 'introduction': {
                this.isReady(true)
                break
            }

            case 'process': {
                this.params.dialog.isClosable?.(false)
                this.isReady(false)
                this.messageData({message: DELETING_MESSAGE})

                const items = _.clone(this.selectedRoles)
                const doNext = (): Promise<any> => {
                    if (!items.length) {
                        return Promise.resolve()
                    }
                    const item = items.shift()
                    this.updateDeletingPercent()
                    return Promise.resolve()
                        .then(() => item.req_purgesystemrolepermissions())
                        .then(() => item.req_deleterole())
                        .then(() => {
                            this.submitSuccess.push(item)
                            this.updateDeletingPercent()
                            return doNext()
                        })
                        .catch((reason: string) => {
                            this.submitError.push({
                                role: item,
                                reason: createErrorString(reason, '_blank'),
                            })
                            return doNext()
                        })
                }

                doNext().then(() => this.step("finish"))

                break
            }

            case 'finish': {
                global.cacheManager.removeCache('searchsystem')
                global.cacheManager.removeCache('searchobject')
                global.cacheManager.removeCache('getobject')
                this.params.dialog.isClosable?.(true)
                this.messageData(null)
                this.isReady(true)
                this.params.dialog.onSuccess?.()
                pubsub.trigger('collection.needs.refresh')
                break
            }
        }
    }

    private isStep(...steps: TStep[]): boolean {
        const step = this.step();
        return step != null && isPartOf(step, ...steps)
    }

    private searchObjectFactory(role: ISystemRole, deleted: TDeleted): (() => Promise<number[]>) {
        const req = global.getPromiseRequest('searchobject')
        return () => {
            this.requests.push(req)
            return req.request(this.getSearchObjectParams(role, deleted))
        }
    }

    private deleteRoleFactory(role: ISystemRole): (() => Promise<IObjectData>) {
        const req = new PromiseRequest('deleterole')
        return () => {
            this.requests.push(req)
            return req.request({
                id: role.id,
            })
        }
    }

    private purgeSystemRolePermissionsFactory(role: ISystemRole): (() => Promise<IObjectData>) {
        const req = new PromiseRequest('purgesystemrolepermissions')
        return () => {
            this.requests.push(req)
            return req.request({
                systemroleid: role.id,
                includeoverrides: this.toggleOverride(),
            })
        }
    }

    private getSearchObjectParams(role: ISystemRole, deleted: TDeleted): ISearchObjectParams {
        const ruleset = nsRuleset.generateRuleset({
            rules: [
                nsRuleset.generateRule({
                    argument: '',
                    argumentoperator: 'Har verdi',
                    attribute: `[OBJECTPERMISSION.SYSTEMROLE;Name;Id;${role.id}]`,
                }),
            ]
        })

        return getSearchObjectParams({
            deleted: deleted,
            jruleset: JSON.stringify(ruleset),
            objecttypeid: ObjectTypeId.User,
            onlyids: true,
            resultsprpage: MAX_RESULTS_PER_PAGE,
        });
    }

    private numAffectedObjectsText(num: number = this.selectedRoles.length): string {
        return nsSystemAccessSearch.numAffectedObjectsText(num)
    }

    private selectedRolesSorted(): ISelectedRole[] {
        return _.sortBy(this.selectedRoles, [(o: ISelectedRole) => o.role.name.toLowerCase(), (o: ISelectedRole) => o.system?.name.toLowerCase() ?? ''])
    }

    private updateLoadingPercent() {
        const loaded = this.selectedRoles.filter(o => o.res_A && o.res_B).length
        const percent = getPercent(loaded, this.selectedRoles.length, {maximumFractionDigits: 0});
        this.messageData({message: `((loading data|td)) ${percent}  ${SPINNER}`})
    }

    private updateDeletingPercent() {
        const deleted = this.submitError.length + this.submitSuccess.length
        const percent = getPercent(deleted, this.selectedRoles.length, {maximumFractionDigits: 0});
        this.messageData({message: `((deleting|td)) ${percent}  ${SPINNER}`})
    }

    dispose() {
        this.requests.forEach(o => o.abort())
        this.req_searchsystem.abort()
    }

}
